import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/features/shared/components/ui/select';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { gatewayApi } from '@/lib/hrApi';

export interface ReportSchedule {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number;
  day_of_month?: number;
  format: 'csv' | 'xlsx';
  channel: 'notification' | 'email' | 'both';
  recipients: string[];
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const getReportSchedule = (id: string) =>
  gatewayApi.get<ReportSchedule>(`/api/v1/reports/definitions/${id}/schedule`).then((r) => r.data);

export const setReportSchedule = (id: string, s: ReportSchedule) =>
  gatewayApi.put<ReportSchedule>(`/api/v1/reports/definitions/${id}/schedule`, s).then((r) => r.data);

/** Scheduled delivery (R7): the run uses the OWNER's frozen permissions;
 *  recipients only ever get an in-app link that executes under their own. */
export const ScheduleReportDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definitionId: string;
  reportName: string;
}> = ({ open, onOpenChange, definitionId, reportName }) => {
  const { t } = useTranslation('reports');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [format, setFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [channel, setChannel] = useState<'notification' | 'email' | 'both'>('both');
  const [recipientsText, setRecipientsText] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getReportSchedule(definitionId)
      .then((s) => {
        setEnabled(!!s.enabled);
        if (s.frequency) setFrequency(s.frequency);
        if (typeof s.day_of_week === 'number') setDayOfWeek(s.day_of_week);
        if (typeof s.day_of_month === 'number' && s.day_of_month >= 1) setDayOfMonth(s.day_of_month);
        if (s.format) setFormat(s.format);
        if (s.channel) setChannel(s.channel);
        setRecipientsText((s.recipients || []).join(', '));
      })
      .catch(() => { /* no schedule yet — defaults stand */ })
      .finally(() => setLoading(false));
  }, [open, definitionId]);

  const save = async () => {
    const recipients = recipientsText.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    if (enabled && recipients.length === 0) {
      setError(t('schedule.recipients_required', { defaultValue: 'Add at least one recipient email.' }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await setReportSchedule(definitionId, {
        enabled, frequency,
        day_of_week: frequency === 'weekly' ? dayOfWeek : undefined,
        day_of_month: frequency === 'monthly' ? dayOfMonth : undefined,
        format, channel, recipients,
      });
      toast.success(enabled
        ? t('schedule.saved', { defaultValue: 'Schedule saved — recipients will get an in-app link when it runs.' })
        : t('schedule.disabled', { defaultValue: 'Schedule turned off.' }));
      onOpenChange(false);
    } catch (e: any) {
      setError(getApiErrorMessage(e, 'Could not save the schedule'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('schedule.title', { defaultValue: 'Schedule “{{name}}”', name: reportName })}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-sm text-muted-foreground">{t('schedule.loading', { defaultValue: 'Loading…' })}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>{t('schedule.enabled', { defaultValue: 'Run automatically' })}</Label>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
            {enabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block">{t('schedule.frequency', { defaultValue: 'Frequency' })}</Label>
                    <Select value={frequency} onValueChange={(v) => setFrequency(v as any)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">{t('schedule.daily', { defaultValue: 'Daily' })}</SelectItem>
                        <SelectItem value="weekly">{t('schedule.weekly', { defaultValue: 'Weekly' })}</SelectItem>
                        <SelectItem value="monthly">{t('schedule.monthly', { defaultValue: 'Monthly' })}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {frequency === 'weekly' && (
                    <div>
                      <Label className="mb-1 block">{t('schedule.day', { defaultValue: 'Day' })}</Label>
                      <Select value={String(dayOfWeek)} onValueChange={(v) => setDayOfWeek(Number(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {frequency === 'monthly' && (
                    <div>
                      <Label className="mb-1 block">{t('schedule.day_of_month', { defaultValue: 'Day of month (1–28)' })}</Label>
                      <Input type="number" min={1} max={28} value={dayOfMonth}
                        onChange={(e) => setDayOfMonth(Math.min(28, Math.max(1, Number(e.target.value) || 1)))} />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block">{t('schedule.format', { defaultValue: 'File format' })}</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as any)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                        <SelectItem value="csv">CSV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1 block">{t('schedule.channel', { defaultValue: 'Deliver via' })}</Label>
                    <Select value={channel} onValueChange={(v) => setChannel(v as any)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">{t('schedule.both', { defaultValue: 'Email + notification' })}</SelectItem>
                        <SelectItem value="email">{t('schedule.email', { defaultValue: 'Email' })}</SelectItem>
                        <SelectItem value="notification">{t('schedule.notification', { defaultValue: 'In-app notification' })}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="mb-1 block">{t('schedule.recipients', { defaultValue: 'Recipients (emails, comma-separated)' })}</Label>
                  <Textarea rows={2} value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)}
                    placeholder="owner@company.co.tz, finance@company.co.tz" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('schedule.privacy_note', {
                      defaultValue: 'Recipients get a link into KaziPeople — the report opens under their own permissions. Files are never attached.',
                    })}
                  </p>
                </div>
              </>
            )}
            {error && <div className="rounded border border-danger bg-danger-soft p-2 text-sm text-danger-strong">{error}</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('schedule.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving ? t('schedule.saving', { defaultValue: 'Saving…' }) : t('schedule.save', { defaultValue: 'Save schedule' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
